import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  TrendingDown, 
  AlertTriangle, 
  DollarSign,
  ShoppingCart,
  Plus,
  BarChart3
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const COLORS = ['#002FA7', '#FF4F00', '#10B981', '#F59E0B', '#8B5CF6'];

export default function DashboardPage() {
  const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);
  const user = auth.getCurrentUser();
  const products = storage.getProductsForUser(user);
  const transactions = storage.getTransactionsForUser(user);
  const categories = storage.getCategories();

  // Calculate metrics
  const metrics = useMemo(() => {
    const totalValue = products.reduce((sum, p) => sum + (p.costPrice * p.quantity), 0);
    const totalProducts = products.length;
    const lowStockItems = products.filter(p => p.quantity <= p.minStock && p.quantity > 0);
    const outOfStockItems = products.filter(p => p.quantity === 0);
    
    const recentTransactions = transactions
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);

    return {
      totalValue,
      totalProducts,
      lowStockItems,
      outOfStockItems,
      recentTransactions
    };
  }, [products, transactions]);

  // Category distribution data
  const categoryData = useMemo(() => {
    const distribution = {};
    products.forEach(product => {
      if (!distribution[product.category]) {
        distribution[product.category] = 0;
      }
      distribution[product.category] += product.costPrice * product.quantity;
    });
    
    return Object.entries(distribution).map(([name, value]) => ({
      name,
      value: Math.round(value)
    }));
  }, [products]);

  // Stock levels by category
  const stockLevelData = useMemo(() => {
    const levels = {};
    products.forEach(product => {
      if (!levels[product.category]) {
        levels[product.category] = { inStock: 0, lowStock: 0, outOfStock: 0 };
      }
      if (product.quantity === 0) {
        levels[product.category].outOfStock++;
      } else if (product.quantity <= product.minStock) {
        levels[product.category].lowStock++;
      } else {
        levels[product.category].inStock++;
      }
    });
    
    return Object.entries(levels).map(([name, counts]) => ({
      name,
      ...counts
    }));
  }, [products]);

  const StatCard = ({ title, value, icon: Icon, color, link }) => (
    <Card className="card-hover">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`text-${color}`} size={20} />
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-black font-heading tracking-tight">{value}</div>
        {link && (
          <Link to={link} className="text-sm text-primary font-medium hover:underline mt-2 inline-block">
            View details →
          </Link>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Overview of your inventory and sales</p>
        </div>
        <div className="flex gap-3">
          <Link to="/products">
            <Button data-testid="add-product-btn" className="font-bold uppercase tracking-wide">
              <Plus size={20} className="mr-2" />
              Add Product
            </Button>
          </Link>
          <Link to="/sales">
            <Button data-testid="record-sale-btn" variant="secondary" className="font-bold uppercase tracking-wide">
              <ShoppingCart size={20} className="mr-2" />
              Record Sale
            </Button>
          </Link>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Inventory Value"
          value={formatINR(metrics.totalValue)}
          icon={DollarSign}
          color="success"
        />
        <StatCard
          title="Total Products"
          value={metrics.totalProducts}
          icon={Package}
          color="primary"
          link="/products"
        />
        <StatCard
          title="Low Stock Items"
          value={metrics.lowStockItems.length}
          icon={TrendingDown}
          color="warning"
          link="/products"
        />
        <StatCard
          title="Out of Stock"
          value={metrics.outOfStockItems.length}
          icon={AlertTriangle}
          color="destructive"
          link="/products"
        />
      </div>

      {/* Low Stock Alerts */}
      {metrics.lowStockItems.length > 0 && (
        <Card className="border-l-4 border-l-warning" data-testid="low-stock-alerts">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="text-warning" size={24} />
              Low Stock Alerts
            </CardTitle>
            <CardDescription>Products that need restocking</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.lowStockItems.map(product => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-warning/10 rounded-sm">
                  <div className="flex items-center gap-3">
                    {product.image && (
                      <img src={product.image} alt={product.name} className="w-12 h-12 object-cover rounded-sm" />
                    )}
                    <div>
                      <p className="font-semibold">{product.name}</p>
                      <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-warning">{product.quantity} units</p>
                    <p className="text-sm text-muted-foreground">Min: {product.minStock}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Out of Stock Alerts */}
      {metrics.outOfStockItems.length > 0 && (
        <Card className="border-l-4 border-l-destructive" data-testid="out-of-stock-alerts">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="text-destructive" size={24} />
              Out of Stock
            </CardTitle>
            <CardDescription>Products that are currently unavailable</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {metrics.outOfStockItems.map(product => (
                <div key={product.id} className="flex items-center justify-between p-3 bg-destructive/10 rounded-sm">
                  <div className="flex items-center gap-3">
                    {product.image && (
                      <img src={product.image} alt={product.name} className="w-12 h-12 object-cover rounded-sm" />
                    )}
                    <div>
                      <p className="font-semibold">{product.name}</p>
                      <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
                    </div>
                  </div>
                  <span className="status-badge status-out-of-stock">Out of Stock</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inventory Value by Category */}
        <Card>
          <CardHeader>
            <CardTitle>Inventory Value by Category</CardTitle>
            <CardDescription>Distribution of inventory value across categories</CardDescription>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatINR(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stock Levels Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Stock Levels by Category</CardTitle>
            <CardDescription>Number of products in each stock status</CardDescription>
          </CardHeader>
          <CardContent>
            {stockLevelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stockLevelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="inStock" fill="#10B981" name="In Stock" />
                  <Bar dataKey="lowStock" fill="#F59E0B" name="Low Stock" />
                  <Bar dataKey="outOfStock" fill="#EF4444" name="Out of Stock" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 size={24} />
            Recent Transactions
          </CardTitle>
          <CardDescription>Last 5 sales transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {metrics.recentTransactions.length > 0 ? (
            <div className="space-y-3">
              {metrics.recentTransactions.map(transaction => (
                <div key={transaction.id} className="flex items-center justify-between p-3 border rounded-sm">
                  <div>
                    <p className="font-semibold">Transaction #{transaction.id.slice(-8)}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(transaction.date).toLocaleString()}
                    </p>
                    {transaction.customer && (
                      <p className="text-sm text-muted-foreground">Customer: {transaction.customer}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg">{formatINR(transaction.total)}</p>
                    <p className="text-sm text-muted-foreground">{transaction.paymentMethod}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No transactions yet. Start recording sales!
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}